import type { PreviewRestaurant } from '../restaurantPreview';

export type CuisineTag = { id?: number | null; title?: string | null; category?: string | null };
export type CuisineOption = { id: number; title: string; slug: string };

export const cuisineSlug = (title: string) =>
  title
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export function cuisineOptions(tags: CuisineTag[]): CuisineOption[] {
  const options = tags.flatMap((tag) =>
    tag.category === 'Cuisine' && tag.id && tag.title && cuisineSlug(tag.title)
      ? [{ id: tag.id, title: tag.title, slug: cuisineSlug(tag.title) }]
      : []
  );
  // Ambiguous names must not silently select a different cuisine.
  const slugCounts = new Map<string, number>();
  options.forEach(({ slug }) => slugCounts.set(slug, (slugCounts.get(slug) || 0) + 1));
  return options.filter(({ slug }) => slugCounts.get(slug) === 1);
}

const MEAL_SUFFIX = /\/(breakfast|lunch|dinner|last-table)$/;

/** The one parse of a city URL: `/<location>[/<meal>][/cuisine/<slug>]`. */
export function parseCuisinePath(url: string) {
  const pathname = url.split(/[?#]/)[0].replace(/\/$/, '') || '/';
  const match = pathname.match(/^(\/.+)\/cuisine\/([^/]+)$/);
  return { pathname, locationPath: match?.[1] || pathname, cuisine: match?.[2] || '' };
}

export function cuisineHref(path: string, slug: string) {
  const { locationPath } = parseCuisinePath(path);
  return `${locationPath.replace(MEAL_SUFFIX, '')}/cuisine/${slug}`;
}

export function cuisineFromPath(path: string, tags: CuisineTag[]) {
  const { cuisine } = parseCuisinePath(path);
  if (!cuisine) return undefined;
  return cuisineOptions(tags).find((tag) => tag.slug === cuisine);
}

export type CuisineEncoding = { pathname: string; cuisineIds: string[] };

/**
 * The one build of where a cuisine selection lives. Exactly one cuisine that has a page on this location, with no meal in the path, is `/cuisine/<slug>`; anything else stays in the `Cuisine` query. Suburb query params never change this. `routes` are the cuisine pages of the location itself; when they are not loaded yet the selection stays in the query so an unverified path can never 404, and the caller builds again once they arrive.
 */
export function buildCuisinePath(
  locationPath: string,
  cuisineIds: string[],
  routes?: CuisineTag[] | null
): CuisineEncoding {
  const location = locationPath.replace(/\/$/, '') || '/';
  const cuisine =
    cuisineIds.length === 1 && routes
      ? cuisineOptions(routes).find((option) => String(option.id) === cuisineIds[0])
      : undefined;
  if (cuisine && !MEAL_SUFFIX.test(location)) {
    return { pathname: `${location}/cuisine/${cuisine.slug}`, cuisineIds: [] };
  }
  return { pathname: location, cuisineIds };
}

export type CuisineLanding = {
  id: number;
  slug: string;
  title: string;
  locationPath: string;
  locationTitle: string;
  restaurants: PreviewRestaurant[];
};
